import React, { useState } from "react";
import {
  Box, Typography, IconButton, Tooltip, Autocomplete, TextField,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { East as EastIcon, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";

const T = {
  glass: {
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
    borderRadius: "12px",
  },
  text: "#1a1d23",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  accent: "#2563eb",
  border: "rgba(0,0,0,0.07)",
  surface: "#f8f9fc",
  amber: {
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.3)",
    text: "#92400e",
    pill: "rgba(245,158,11,0.14)",
  },
};

const GEO_LEVELS = [
  { val: "electorate", label: "Electorate" },
  { val: "SA3", label: "SA3" },
  { val: "SA2", label: "SA2", requiresSelection: true },
];

// ── NZ parliament (MMP, 120 seats) ─────────────────────────────────────────
const NZ_PARTY_ORDER = [
  "tpm_pred_n", "green_pred_n", "lab_pred_n",
  "nzf_pred_n", "nat_pred_n", "act_pred_n", "oth_pred_n",
];
const NZ_SEATS = 120;
const NZ_MAJORITY = 61;

const calculateNZSeats = (rows, keys, waveSelect) => {
  const filtered = waveSelect != null
    ? rows.filter((r) => Number(r.wave ?? r.Wave) === Number(waveSelect))
    : rows;
  if (!filtered.length) return null;

  const partyKeys = keys.filter((k) => !k.includes("oth"));
  const avgs = {};
  partyKeys.forEach((k) => {
    const vals = filtered.map((r) => Number(r[k])).filter(Number.isFinite);
    avgs[k] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  });
  const total = Object.values(avgs).reduce((a, b) => a + b, 0);
  if (!total) return null;

  // Largest-remainder method to ensure seats sum exactly to NZ_SEATS
  const items = partyKeys.map((k) => {
    const raw = (avgs[k] / total) * NZ_SEATS;
    return { k, seats: Math.floor(raw), remainder: raw - Math.floor(raw) };
  });
  const remaining = NZ_SEATS - items.reduce((a, b) => a + b.seats, 0);
  items.sort((a, b) => b.remainder - a.remainder);
  items.forEach((p, i) => { if (i < remaining) p.seats += 1; });

  return Object.fromEntries(items.map(({ k, seats }) => [k, seats]));
};

const ParliamentPlot = ({ seatCounts, colors, partyOrder, rows = 6, dotRadius = 4, width = 220, height = 115 }) => {
  if (!seatCounts || !colors) return null;
  const orderedKeys = (partyOrder || Object.keys(seatCounts)).filter((k) => (seatCounts[k] || 0) > 0);
  const seatsSeq = [];
  orderedKeys.forEach((k) => { for (let i = 0; i < seatCounts[k]; i++) seatsSeq.push(k); });
  if (!seatsSeq.length) return null;

  const cx = width / 2, cy = height - 6;
  const pitch = dotRadius * 2.6;
  const outerR = Math.min(width / 2 - 12, 95);
  const radii = Array.from({ length: rows }, (_, i) => outerR - i * pitch * 1.15);
  const capacities = radii.map((r) => Math.max(1, Math.floor(Math.PI * r / pitch)));
  const totalCapacity = capacities.reduce((a, b) => a + b, 0);
  const scaling = NZ_SEATS / totalCapacity;
  const scaledCapacities = capacities.map((c) => Math.ceil(c * scaling));

  const slots = [];
  radii.forEach((r, row) => {
    const n = scaledCapacities[row];
    for (let j = 0; j < n; j++) {
      const t = (j + 0.5) / n;
      const angle = Math.PI * t;
      slots.push({ angle, row, x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) });
    }
  });
  slots.sort((a, b) => (a.angle - b.angle) || (a.row - b.row));
  const useSlots = slots.slice(0, NZ_SEATS);
  const filled = useSlots.map((s, i) => ({ ...s, color: colors[seatsSeq[i % seatsSeq.length]] || "#999" }));

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block", margin: "0 auto" }}>
      {filled.map((s, i) => <circle key={i} cx={s.x} cy={s.y} r={dotRadius} fill={s.color} opacity={0.92} />)}
    </svg>
  );
};

const SeatTable = ({ seatCounts, labels, colors }) => {
  if (!seatCounts) return null;
  let entries = Object.entries(seatCounts).filter(([, n]) => n > 0);
  const otherEntry = entries.find(([k]) => k.includes("oth"));
  entries = entries.filter(([k]) => !k.includes("oth")).sort((a, b) => b[1] - a[1]);
  if (otherEntry) entries.push(otherEntry);
  return (
    <table style={{ fontSize: 11, borderCollapse: "collapse", margin: "0 auto", width: "100%" }}>
      <tbody>
        {entries.map(([k, n]) => (
          <tr key={k}>
            <td style={{ padding: "2px 6px", whiteSpace: "nowrap" }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 5, backgroundColor: colors?.[k] || "#999" }} />
              <span style={{ color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>{labels?.[k] || k}</span>
            </td>
            <td style={{ padding: "2px 6px", textAlign: "right", fontWeight: 700, color: T.text, whiteSpace: "nowrap", fontFamily: "'DM Sans', system-ui, sans-serif" }}>{n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MapToolbar = ({
  geoLevel, onGeoLevelChange,
  selectedAreaLabel, onClearSelection,
  PossibleAreas, onAreaSelect,
  selectedDemographics, onResetDemographics,
  zoomIn, zoomOut, resetView,
  waveOptions, waveSelect, handlewaveSelectChange,
  showArrows, onShowArrowsToggle,
  compareWaves, onCompareWavesChange,
  compareKey, onCompareKeyChange,
  active_group,
  actionMode,
  metricRowsElectorates,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  React.useEffect(() => {
    if (selectedAreaLabel) setSearchOpen(false);
  }, [selectedAreaLabel]);

  const hasWaves = waveOptions?.length > 0;
  const sortedWaves = [...(waveOptions || [])].sort((a, b) => a - b);
  const prevWave = sortedWaves[sortedWaves.length - 2];
  const latestWave = sortedWaves[sortedWaves.length - 1];
  const canCompare = waveOptions?.length >= 2;

  const isTargetMode = actionMode === "filter";
  const displayKeys = active_group?.keys || [];

  const showParliament = active_group?.label === "Voting Intention" && metricRowsElectorates?.length > 0;
  const seatCounts = React.useMemo(() => {
    if (!showParliament || !active_group?.keys?.length) return null;
    return calculateNZSeats(metricRowsElectorates, active_group.keys, waveSelect);
  }, [showParliament, metricRowsElectorates, active_group?.keys, waveSelect]);
  const getLabel = (key) => active_group?.labels?.[key] || key;
  const getColor = (key) => active_group?.colors?.[key];

  const compareMode = compareKey === "__top__" ? "top_pos"
    : compareKey === "__top_neg__" ? "top_neg"
    : "selected";
  const selectedCategoryKey = displayKeys.includes(compareKey)
    ? compareKey
    : (displayKeys[0] || null);

  const demoPills = [];
  if (selectedDemographics) {
    Object.entries(selectedDemographics).forEach(([key, val]) => {
      const lbls = Array.isArray(val) ? val : val ? [val] : [];
      lbls.forEach((l) => demoPills.push({ key: `${key}-${l}`, label: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}` }));
    });
  }
  const hasFilters = demoPills.length > 0;

  return (
    <>
      {/* ── Top-left: geo level pill + collapsible search ─────────────────── */}
      <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
        {!searchOpen ? (
          <Box sx={{ display: "flex", gap: 0.75 }}>
            {/* Geo level pill */}
            <Box sx={{ ...T.glass, display: "flex", alignItems: "center", px: 0.5, py: 0.5, gap: 0.25 }}>
              {GEO_LEVELS.map(({ val, label, requiresSelection }, i, arr) => {
                const disabled = requiresSelection && !selectedAreaLabel;
                return (
                  <React.Fragment key={val}>
                    <Tooltip title={disabled ? "Select an area first" : ""} placement="bottom" arrow>
                      <Box
                        onClick={() => !disabled && onGeoLevelChange?.(val)}
                        sx={{
                          px: 1.25, py: 0.55, borderRadius: "7px",
                          bgcolor: geoLevel === val ? T.accent : "transparent",
                          color: geoLevel === val ? "#fff" : disabled ? T.textFaint : T.textMuted,
                          fontSize: "12px", fontWeight: 600,
                          fontFamily: "'DM Sans', system-ui, sans-serif",
                          cursor: disabled ? "default" : "pointer",
                          opacity: disabled ? 0.45 : 1,
                          transition: "all 0.12s",
                          userSelect: "none",
                          "&:hover": (!disabled && geoLevel !== val) ? { bgcolor: "rgba(0,0,0,0.05)", color: T.text } : {},
                        }}
                      >
                        {label}
                      </Box>
                    </Tooltip>
                    {i < arr.length - 1 && (
                      <Box sx={{ width: "1px", height: 18, bgcolor: T.border, flexShrink: 0 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {/* Search trigger */}
            <Tooltip title="Find area" placement="right">
              <Box
                onClick={() => setSearchOpen(true)}
                sx={{
                  ...T.glass,
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.25, py: 0.65, cursor: "pointer",
                  transition: "all 0.12s",
                  "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.12), 0 0 0 2px rgba(37,99,235,0.15)" },
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: T.textMuted }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                  Find {geoLevel === "electorate" ? "electorate" : geoLevel}…
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        ) : (
          /* Expanded search */
          <Box sx={{
            ...T.glass,
            display: "flex", alignItems: "center", gap: 1,
            px: 1.25, py: 0.75, width: 380,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 0 2px rgba(37,99,235,0.18)",
          }}>
            <SearchIcon sx={{ fontSize: 17, color: T.accent, flexShrink: 0 }} />
            <Autocomplete
              open
              disablePortal
              autoFocus
              options={PossibleAreas || []}
              getOptionLabel={(o) => o.area_name || ""}
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", fontSize: "13px",
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  "& fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": { borderColor: "transparent" },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder={`Find ${geoLevel}…`}
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { height: 34, bgcolor: "transparent" },
                    "& .MuiOutlinedInput-input": { padding: "4px 4px", fontSize: "13px" },
                  }}
                />
              )}
              onChange={(_, newValue) => {
                setSearchOpen(false);
                if (!newValue) { resetView?.(); return; }
                onAreaSelect?.({ id: newValue.area_id, name: newValue.area_name });
              }}
              onBlur={() => setSearchOpen(false)}
            />
            <Tooltip title="Close" placement="right">
              <IconButton
                size="small"
                onClick={() => setSearchOpen(false)}
                sx={{ color: T.textFaint, flexShrink: 0, "&:hover": { color: T.text } }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* ── Top-right: selected area card + demographic filter pills ──────── */}
      {(selectedAreaLabel || hasFilters) && (
        <Box sx={{
          position: "absolute", top: 16, right: 16, zIndex: 10,
          ...T.glass,
          minWidth: 220, maxWidth: 300,
          overflow: "hidden",
          ...(hasFilters ? {
            border: `1px solid ${T.amber.border}`,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 0 0 3px rgba(245,158,11,0.1)",
          } : {}),
        }}>
          {selectedAreaLabel && (
            <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.2 }}>
                  Selected area
                </Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.text, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                  {selectedAreaLabel}
                </Typography>
              </Box>
              <Tooltip title="Clear selection" placement="bottom">
                <IconButton
                  size="small"
                  onClick={onClearSelection}
                  sx={{ width: 28, height: 28, borderRadius: "7px", border: `1px solid ${T.border}`, bgcolor: T.surface, color: T.textMuted, "&:hover": { bgcolor: "rgba(220,38,38,0.06)", borderColor: "rgba(220,38,38,0.3)", color: "#dc2626" } }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {hasFilters && (
            <>
              {selectedAreaLabel && <Box sx={{ height: "1px", bgcolor: T.amber.border, mx: 1.5 }} />}
              <Box sx={{ px: 1.5, py: 0.85, bgcolor: T.amber.bg }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 11, color: T.amber.text }} />
                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.amber.text, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Filtered
                    </Typography>
                  </Box>
                  <Tooltip title="Clear all filters" placement="bottom">
                    <IconButton
                      size="small"
                      onClick={onResetDemographics}
                      sx={{ width: 20, height: 20, color: T.amber.text, opacity: 0.7, "&:hover": { opacity: 1 } }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                  {demoPills.map(({ key, label }) => (
                    <Box
                      key={key}
                      sx={{
                        fontSize: "10px", fontWeight: 600, color: T.amber.text,
                        bgcolor: T.amber.pill, border: `1px solid ${T.amber.border}`,
                        borderRadius: "5px", px: 0.75, py: 0.2, lineHeight: 1.6,
                        fontFamily: "'DM Sans', system-ui, sans-serif",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── Left column: zoom controls ─────────────────────────────────────── */}
      <Box sx={{
        position: "absolute", left: 16, top: 64, bottom: 16, zIndex: 10,
        display: "flex", flexDirection: "column", alignItems: "flex-start",
        pointerEvents: "none",
      }}>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", pointerEvents: "none" }}>
          <Box sx={{
            ...T.glass,
            display: "flex", flexDirection: "column",
            p: 0.5, gap: 0, overflow: "hidden",
            pointerEvents: "auto",
          }}>
            {[
              { icon: <ZoomInIcon sx={{ fontSize: 18 }} />, onClick: zoomIn, title: "Zoom in" },
              { icon: <ZoomOutIcon sx={{ fontSize: 18 }} />, onClick: zoomOut, title: "Zoom out" },
              { icon: <RestartAltIcon sx={{ fontSize: 18 }} />, onClick: resetView, title: "Reset view" },
            ].map(({ icon, onClick, title }, i, arr) => (
              <React.Fragment key={title}>
                <Tooltip title={title} placement="right">
                  <IconButton
                    onClick={onClick}
                    size="small"
                    sx={{
                      width: 34, height: 34, borderRadius: "7px",
                      color: T.textMuted,
                      "&:hover": { bgcolor: "rgba(37,99,235,0.07)", color: T.accent },
                      transition: "all 0.12s",
                    }}
                  >
                    {icon}
                  </IconButton>
                </Tooltip>
                {i < arr.length - 1 && <Box sx={{ height: "1px", width: "100%", bgcolor: T.border }} />}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {showParliament && seatCounts && (
          <Box sx={{ ...T.glass, px: 1.5, pt: 1.25, pb: 1.25, width: 230, pointerEvents: "auto" }}>
            <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5, textAlign: "center" }}>
              Projected seats (120)
            </Typography>
            <ParliamentPlot
              seatCounts={seatCounts}
              colors={active_group.colors}
              partyOrder={NZ_PARTY_ORDER}
            />
            <Box sx={{ mt: 0.75 }}>
              <SeatTable seatCounts={seatCounts} labels={active_group.labels} colors={active_group.colors} />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Bottom-right: wave controls ────────────────────────────────────── */}
      {hasWaves && (
        <Box sx={{
          position: "absolute", bottom: 10, right: 16, zIndex: 10,
          ...T.glass,
          width: 248, overflow: "hidden",
        }}>
          {!compareWaves ? (
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25 }}>
              <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                Wave
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: canCompare ? 1.25 : 0 }}>
                {sortedWaves.map((wave) => (
                  <Box
                    key={wave}
                    onClick={() => handlewaveSelectChange?.(wave)}
                    sx={{
                      px: 1.25, py: 0.45, borderRadius: "7px",
                      border: "1px solid",
                      borderColor: waveSelect === wave ? T.accent : T.border,
                      bgcolor: waveSelect === wave ? "rgba(37,99,235,0.08)" : T.surface,
                      color: waveSelect === wave ? T.accent : T.textMuted,
                      fontSize: "12px", fontWeight: waveSelect === wave ? 700 : 500,
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      cursor: "pointer", transition: "all 0.12s",
                      "&:hover": { borderColor: T.accent, color: T.accent },
                    }}
                  >
                    {wave}
                  </Box>
                ))}
              </Box>
              {canCompare && (
                <Box
                  onClick={() => onCompareWavesChange?.(true)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                    px: 1, py: 0.7, borderRadius: "8px",
                    border: `1px solid ${T.border}`,
                    bgcolor: T.surface, cursor: "pointer",
                    transition: "all 0.12s",
                    "&:hover": { borderColor: T.accent, bgcolor: "rgba(37,99,235,0.05)" },
                  }}
                >
                  <EastIcon sx={{ fontSize: 13, color: T.textFaint }} />
                  <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    Compare waves
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <>
              {/* Compare mode header */}
              <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "rgba(37,99,235,0.04)", borderBottom: `1px solid ${T.border}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                      Wave {prevWave}
                    </Typography>
                    <EastIcon sx={{ fontSize: 12, color: T.accent }} />
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                      Wave {latestWave}
                    </Typography>
                  </Box>
                  <Tooltip title={showArrows !== false ? "Hide movement arrows" : "Show movement arrows"} placement="top">
                    <Box
                      onClick={() => onShowArrowsToggle?.()}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: 26, height: 26, borderRadius: "6px",
                        border: "1px solid",
                        borderColor: showArrows !== false ? T.accent : T.border,
                        bgcolor: showArrows !== false ? "rgba(37,99,235,0.08)" : "transparent",
                        color: showArrows !== false ? T.accent : T.textFaint,
                        cursor: "pointer", transition: "all 0.12s",
                        "&:hover": { borderColor: T.accent, color: T.accent },
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: 12, transform: "rotate(-45deg)" }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Tooltip title="Exit compare" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => onCompareWavesChange?.(false)}
                    sx={{ width: 24, height: 24, color: T.textFaint, "&:hover": { color: "#dc2626", bgcolor: "rgba(220,38,38,0.06)" } }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Compare controls — only when group has multiple keys */}
              {active_group?.keys?.length > 1 && (
                <Box sx={{ px: 1.5, py: 1.25 }}>
                  {!isTargetMode && (
                    <>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                        Map mode
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 1.25 }}>
                        {[
                          { val: "top_pos", label: "Biggest gainer" },
                          { val: "top_neg", label: "Biggest loser" },
                          { val: "selected", label: "By category" },
                        ].map(({ val, label }) => (
                          <Box
                            key={val}
                            onClick={() => {
                              if (val === "top_pos") onCompareKeyChange?.("__top__");
                              else if (val === "top_neg") onCompareKeyChange?.("__top_neg__");
                              else onCompareKeyChange?.(selectedCategoryKey || "__top__");
                            }}
                            sx={{
                              flex: 1, textAlign: "center",
                              px: 0.5, py: 0.55, borderRadius: "7px",
                              border: "1px solid",
                              borderColor: compareMode === val ? T.accent : T.border,
                              bgcolor: compareMode === val ? "rgba(37,99,235,0.08)" : T.surface,
                              color: compareMode === val ? T.accent : T.textMuted,
                              fontSize: "10.5px", fontWeight: compareMode === val ? 700 : 500,
                              fontFamily: "'DM Sans', system-ui, sans-serif",
                              cursor: "pointer", lineHeight: 1.3,
                              transition: "all 0.12s",
                              "&:hover": { borderColor: T.accent, color: T.accent },
                            }}
                          >
                            {label}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {!isTargetMode && compareMode === "selected" && (
                    <>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.65 }}>
                        {active_group?.label || "Category"}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45, mb: 1.25 }}>
                        {displayKeys.map((key) => {
                          const col = getColor(key);
                          const lbl = getLabel(key);
                          const isActive = compareKey === key || (!displayKeys.includes(compareKey) && key === selectedCategoryKey);
                          return (
                            <Box
                              key={key}
                              onClick={() => onCompareKeyChange?.(key)}
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: 0.5,
                                px: 0.85, py: 0.35, borderRadius: "6px",
                                border: "1px solid",
                                borderColor: isActive ? (col || T.accent) : T.border,
                                bgcolor: isActive ? (col ? `${col}18` : "rgba(37,99,235,0.07)") : "transparent",
                                color: isActive ? (col || T.accent) : T.textMuted,
                                fontSize: "10.5px", fontWeight: isActive ? 700 : 500,
                                fontFamily: "'DM Sans', system-ui, sans-serif",
                                cursor: "pointer", transition: "all 0.12s",
                                "&:hover": { borderColor: col || T.accent, color: col || T.accent },
                              }}
                            >
                              {col && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />}
                              {lbl}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {/* Map key */}
                  <Box sx={{ pt: 1, borderTop: `1px solid ${T.border}` }}>
                    <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.6 }}>
                      Map key
                    </Typography>
                    {isTargetMode || compareMode === "selected" ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        {[
                          { color: "#15803d", label: "Gaining support" },
                          { color: "#e5e7eb", label: "Stable / no change" },
                          { color: "#b91c1c", label: "Losing support" },
                        ].map(({ color, label }) => (
                          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "10.5px", color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        {displayKeys.map((key) => {
                          const col = getColor(key);
                          if (!col) return null;
                          return (
                            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box sx={{ width: 10, height: 6, borderRadius: "2px", bgcolor: col, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: "10.5px", color: T.textMuted, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                                {getLabel(key)}
                              </Typography>
                            </Box>
                          );
                        })}
                        <Typography sx={{ mt: 0.25, fontSize: "9.5px", color: T.textFaint, fontStyle: "italic" }}>
                          Shade = movement strength
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default MapToolbar;
